import React, { useState, useCallback } from 'react';
import { Button } from '../../components/atoms';
import { useInternationalContentActions } from '../../hooks/useInternationalContent';
import { DeleteConfirmationModal } from '../../components/organisms/DeleteConfirmationModal';
import toast from 'react-hot-toast';

export const SettingsPage: React.FC = () => {
  const { activateInternationalContent, deactivateInternationalContent } =
    useInternationalContentActions();

  const [isActivating, setIsActivating] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);

  const handleActivate = useCallback(async () => {
    setIsActivating(true);
    try {
      const loadingToast = toast.loading('Activating international content...');
      await activateInternationalContent();
      toast.dismiss(loadingToast);
    } finally {
      setIsActivating(false);
    }
  }, [activateInternationalContent]);

  const handleDeactivateClick = useCallback(() => {
    setIsDeactivateModalOpen(true);
  }, []);

  const handleDeactivateConfirm = useCallback(async () => {
    setIsDeactivating(true);
    try {
      const loadingToast = toast.loading('Deactivating international content...');
      await deactivateInternationalContent();
      toast.dismiss(loadingToast);
      setIsDeactivateModalOpen(false);
    } finally {
      setIsDeactivating(false);
    }
  }, [deactivateInternationalContent]);

  const handleDeactivateModalClose = useCallback(() => {
    if (!isDeactivating) {
      setIsDeactivateModalOpen(false);
    }
  }, [isDeactivating]);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-600 mt-1">
          Manage application and content settings
        </p>
      </div>

      {/* International Content Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0h.5a2.5 2.5 0 002.5-2.5V8m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              {/* <h2 className="text-lg font-semibold text-gray-900">
                International Movies
              </h2> */}
              <p className="mt-1 text-sm text-left text-gray-600">
                Control visibility of international content across the platform.
                When deactivated, international movies will be hidden from users.
                When activated, they will be visible.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Button
                  variant="primary"
                  onClick={handleActivate}
                  isLoading={isActivating}
                  disabled={isDeactivating}
                  leftIcon={
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  }
                >
                  Activate
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDeactivateClick}
                  isLoading={isDeactivating}
                  disabled={isActivating}
                  leftIcon={
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  }
                >
                  Deactivate
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Deactivate Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeactivateModalOpen}
        onClose={handleDeactivateModalClose}
        onConfirm={handleDeactivateConfirm}
        title="Deactivate International Content"
        message="Are you sure you want to deactivate international movies? They will be hidden from users until you activate them again."
        confirmLabel="Deactivate"
        isDeleting={isDeactivating}
      />
    </div>
  );
};
